/**
 * Local calendar dates as YYYY-MM-DD (API / datepickers), interpreted in the browser's local timezone.
 * Avoids `new Date("YYYY-MM-DD")`, which parses as UTC midnight and can shift the calendar day.
 *
 * Lives alongside {@link DateFormatService} in `core/date/` — that service handles timezone-aware datetimes;
 * these functions are for plain calendar strings without IANA conversion.
 */

const SHORT_MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * Parse a YYYY-MM-DD string into a Date at local midnight. Returns null if invalid.
 */
export function parseLocalDateFromYyyyMmDd(
  dateStr: string | null | undefined
): Date | null {
  if (dateStr == null || String(dateStr).trim() === '') {
    return null;
  }
  const parts = String(dateStr).trim().split('-');
  if (parts.length !== 3) {
    return null;
  }
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    return null;
  }
  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }
  const d = new Date(year, month - 1, day, 0, 0, 0, 0);
  if (
    d.getFullYear() !== year ||
    d.getMonth() !== month - 1 ||
    d.getDate() !== day
  ) {
    return null;
  }
  return d;
}

/** Format a local calendar date as YYYY-MM-DD for API query params and payloads. */
export function formatLocalDateToYyyyMmDd(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Display string like "Jan 15, 2026" for a YYYY-MM-DD value; invalid input returns the original string. */
export function formatYyyyMmDdHumanFriendly(
  dateString: string | null | undefined
): string {
  if (!dateString) {
    return '';
  }
  const date = parseLocalDateFromYyyyMmDd(dateString);
  if (!date) {
    return dateString;
  }
  const month = SHORT_MONTH_NAMES[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  return `${month} ${day}, ${year}`;
}

/** ``YYYY-MM`` or ``YYYY-MM-DD`` → ``January 2026`` (billing month jump labels). */
export function formatYearMonthLabel(
  yearMonth: string | null | undefined
): string {
  if (!yearMonth?.trim()) {
    return '';
  }
  const ymd =
    yearMonth.length >= 10
      ? yearMonth.slice(0, 10)
      : `${yearMonth.slice(0, 7)}-01`;
  const date = parseLocalDateFromYyyyMmDd(ymd);
  if (!date) {
    return yearMonth.slice(0, 7);
  }
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/** True when ``asOf`` (default today, local calendar) falls within ``start``–``end`` (YYYY-MM-DD). */
export function localDateRangeIncludesToday(
  start: string | null | undefined,
  end: string | null | undefined,
  asOf: Date = new Date()
): boolean {
  const startDate = parseLocalDateFromYyyyMmDd(start?.slice(0, 10));
  const endDate = parseLocalDateFromYyyyMmDd(end?.slice(0, 10));
  if (!startDate || !endDate) {
    return false;
  }
  const today = new Date(asOf.getFullYear(), asOf.getMonth(), asOf.getDate());
  return startDate <= today && today <= endDate;
}

/**
 * Calendar date from an ISO-8601 instant as ``YYYY-MM-DD``.
 *
 * Uses the date portion before ``T`` as printed in the string (no timezone conversion).
 * Suitable for pipeline metadata (e.g. ``attempted_at``) when you want a compact wire-style date.
 */
export function formatIso8601DateOnly(iso: string | null | undefined): string {
  const raw = iso?.trim();
  if (!raw) {
    return '';
  }
  const datePart = raw.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : '';
}
